import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { dispatch } from '@ngneat/effects';
import { filter, map } from 'rxjs';

import { setAuthState, verifyAuthSession } from '../repositories/auth.actions';
import {
  authConfigSnapshot,
  authState$,
  authStoreSnapshot,
  authUserSnapshot,
} from '../repositories/auth.repository';
import { debugMessage } from '../utils/debug-message';

/**
 * Use this to guard a Route against unauthenticated Users. This Guard will check for an Authenticated
 * User and will make a verify call to SSO to check for a current session if a User is not currently
 * authenticated.
 *
 * The Guard will also redirect the User to a provided terms of use Route if the authenticated User has
 * not accepted the Terms of Use
 *
 * @param paths.termsRoutePath The Route path to redirect the User if they have not accepted the Terms of Use
 * @param paths.landingPageRoutePath The optional landing page route to redirect the User to if they are not authenticated
 * @returns A CanActivateFn that protects a route against unauthenticated Users
 */
export const AuthGuard = (paths?: {
  landingPageRoutePath?: string;
  termsRoutePath?: string;
}): CanActivateFn => {
  return (_: ActivatedRouteSnapshot, routerState: RouterStateSnapshot) => {
    const router = inject(Router);
    const hasTermsPath = typeof paths?.termsRoutePath !== 'undefined';
    const termsRoutePath = router.parseUrl(paths?.termsRoutePath ?? '');
    const notAuthenticatePath = router.parseUrl(
      paths?.landingPageRoutePath ?? 'auth/login'
    );
    debugMessage(
      'Auth Guard',
      `Checking if user is permitted to access ${routerState.url}`
    );

    if (authStoreSnapshot().authState.state === 'authenticated') {
      if (routerState.url === termsRoutePath.toString() && hasTermsPath) {
        debugMessage(
          'Auth Guard',
          `User is authenticated and permitted to access ${routerState.url}`
        );
        return true;
      }
    } else if (authStoreSnapshot().authState.state === 'idle') {
      debugMessage(
        'Auth Guard',
        'User is not authenticated. Dispatching verify call to check for auth session.'
      );
      dispatch(
        setAuthState({
          authState: {
            state: 'authenticating-user',
          },
        })
      );
      dispatch(
        verifyAuthSession({
          appName: authConfigSnapshot().trimbleIdentity.trimbleMapsAppName,
        })
      );
    }

    return authState$.pipe(
      filter(({ state }) => state === 'authenticated' || state === 'idle'),
      map((authState) => {
        if (authState.state !== 'authenticated') {
          if (routerState.url === termsRoutePath.toString() && hasTermsPath) {
            debugMessage(
              'Auth Guard',
              `Auth session failed verification, but user is permitted to access ${routerState.url}`
            );
            return true;
          }

          debugMessage(
            'Auth Guard',
            `Auth session failed verification. Redirecting user to ${notAuthenticatePath.toString()}`
          );
          return notAuthenticatePath;
        }

        const user = authUserSnapshot();

        if (!user.currentTermsAccepted && hasTermsPath) {
          debugMessage(
            'Auth Guard',
            `User is authenticated, but has not accepted the terms of service. Redirecting to ${termsRoutePath.toString()}`
          );
          return termsRoutePath;
        }

        debugMessage(
          'Auth Guard',
          `User is authenticated and permitted to access ${routerState.url}`
        );
        return true;
      })
    );
  };
};
